import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { ContentPlanRootState } from '../../elements/contentPlan/store';
import { ElementAction } from '../../elements/elementAction';
import { Functions } from '../../types/types';
import { View } from '../../types/View';
import { setLoading } from '../Loader/loaderAction';
import { reloadWorkspace } from '../ContentPlanning/contentPlanningActions';
import { clearAlert, setError } from '../Alert/alertAction';
import { Workspace } from '../../types/Metadata';
import { useNavigate } from 'react-router-dom';
import URLManager from '../../utils/urlManager';
import { updateViewState } from '../View/viewReducer';

type S = ContentPlanRootState;

const onDrop =
  (): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const { planning } = getState();
    const workspaceRefId = planning?.selectedWorkspace?.props?.refId;
    const previewFolders = planning?.previewFolders;
    const previewRootViews = planning?.previewRootViews;
    const { moveView } = getAPI();
    const folders = previewFolders
      ?.filter(folder => folder?.props?.refId)
      ?.map(folder => ({
        ...folder,
        views: (folder.views ?? []).map(({ dataSchema, ...rest }) => rest)
      }));
    const views = previewRootViews?.map(
      ({ dataSchema, ...cleanedViews }) => cleanedViews
    );
    try {
      dispatch(setLoading(true, 'navDragAndDrop'));
      await moveView({
        workspaceRefId,
        views,
        folders
      });
      dispatch(reloadWorkspace(workspaceRefId));
    } catch (err) {
      console.error(err);
    } finally {
      dispatch(setLoading(false, 'navDragAndDrop'));
    }
  };

const save =
  (
    folderName: string,
    callback: Function,
    folderToUpdate?: View.Folder
  ): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const { planning } = getState();
    const workspaceRefId = planning?.selectedWorkspace?.props?.refId;
    const { createFolder, updateFolder, getWorkspaces } = getAPI();
    folderName = folderName?.trim();
    try {
      dispatch(setLoading(true, 'folderModalLoader'));
      let folderRefId = folderToUpdate?.props?.refId;
      const upsertFn = folderRefId ? updateFolder : createFolder;
      const results = await upsertFn({
        workspaceRefId,
        folderName,
        folderRefId
      });
      if (results?.length) {
        dispatch(reloadWorkspace());
        callback();
      } else {
        throw new Error(
          `Failed to update folder, please contact CPP Help if this issue persists.`
        );
      }
    } catch (err) {
      dispatch(setError(err.message, 'folderModalAlert'));
    } finally {
      dispatch(setLoading(false, 'folderModalLoader'));
    }
  };

const deleteFolder =
  (folderToDeleteRefId: string): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const { planning } = getState();
    const { workspaces, ...props } = planning;
    const { deleteFolder } = getAPI();
    try {
      const results = await deleteFolder({ folderRefId: folderToDeleteRefId });
      if (results?.length) {
        dispatch(reloadWorkspace());
      } else {
        throw new Error(
          `Failed to delete folder, please contact CPP Help if this issue persists.`
        );
      }
    } catch (err) {
      console.error(err);
    }
  };

const cleanUpFolderModalAlerts =
  (): ElementAction<Functions, S> => dispatch => {
    dispatch(clearAlert('folderModalAlert'));
    dispatch(clearAlert('folderUpdateModalAlert'));
  };

const resetEditMode =
  (): ElementAction<Functions, S> => (dispatch, getState) => {
    const viewState = getState().view;
    if (viewState?.selected) {
      dispatch(updateViewState(viewState.selected, { editMode: false }));
    }
  };

export const useActions = () => {
  const dispatch: Dispatch<any> = useDispatch();
  const navigate = useNavigate();
  return {
    navigate: (
      w: Workspace,
      params?: { v?: string; path?: string; id?: string }
    ) => {
      dispatch(resetEditMode());
      navigate(URLManager.generateUrl(w, params));
    },
    onDrop: () => dispatch(onDrop()),
    save: (
      folderName: string,
      callback: Function,
      folderToUpdate?: View.Folder
    ) => dispatch(save(folderName, callback, folderToUpdate)),
    deleteFolder: (folderToDeleteRefId: string) =>
      dispatch(deleteFolder(folderToDeleteRefId)),
    cleanUpFolderModalAlerts: () => dispatch(cleanUpFolderModalAlerts()),
    resetEditMode: () => dispatch(resetEditMode())
  };
};
